/**
 * Auto-updater. Ensures users always see the latest deployed version:
 *  1) On app boot, fetch /api/version and compare with stored value.
 *     If it changed since last visit, hard-reload once so the new bundle loads.
 *  2) On service-worker registration, force an update check, and when a new SW
 *     is installed and waiting, tell it to activate immediately + reload page.
 *  3) Also re-check version whenever the tab becomes visible again.
 */

const VERSION_KEY = '__salaam_app_version';
const CHECK_KEY = '__salaam_last_check';

function apiOrigin() {
  if (typeof window !== 'undefined' && window.location && window.location.origin) return window.location.origin;
  return process.env.REACT_APP_BACKEND_URL || '';
}

async function fetchServerVersion() {
  try {
    const res = await fetch(`${apiOrigin()}/api/version`, { cache: 'no-store' });
    if (!res.ok) return null;
    const d = await res.json();
    return d && d.version ? String(d.version) : null;
  } catch (e) {
    return null;
  }
}

let reloading = false;
function safeReload() {
  if (reloading) return;
  reloading = true;
  // Cache-bust query so any cached HTML gets re-fetched
  const sep = window.location.href.includes('?') ? '&' : '?';
  const url = window.location.href.replace(/[?&]_v=\d+/, '') + `${sep}_v=${Date.now()}`;
  window.location.replace(url);
}

async function checkVersion() {
  const stored = localStorage.getItem(VERSION_KEY);
  const current = await fetchServerVersion();
  if (!current) return;
  if (!stored) {
    localStorage.setItem(VERSION_KEY, current);
    return;
  }
  if (stored !== current) {
    localStorage.setItem(VERSION_KEY, current);
    safeReload();
  }
}

export function installAutoUpdater() {
  if (typeof window === 'undefined') return;

  // 1) Version check on boot (small delay to let app render first)
  setTimeout(checkVersion, 800);

  // 2) Re-check when tab becomes visible again (user reopens PWA / switches back)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    const last = parseInt(localStorage.getItem(CHECK_KEY) || '0', 10);
    if (now - last < 15000) return; // throttle to at most once per 15s
    localStorage.setItem(CHECK_KEY, String(now));
    checkVersion();
  });

  // 3) Service-worker update loop — check for a waiting SW and activate it
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) return;
      // Force an update check now
      try { reg.update(); } catch (e) { /* ignore */ }

      // If a new worker is already waiting, activate it
      if (reg.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller) {
            // New SW installed — activate and reload
            try { nw.postMessage({ type: 'SKIP_WAITING' }); } catch (e) { /* ignore */ }
          }
        });
      });
    }).catch(() => {});

    // Reload once when a new SW takes control
    let didReload = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (didReload) return;
      didReload = true;
      safeReload();
    });
  }
}
